import { canUseDOM } from 'exenv';

export type MobilePlatform = 'ios' | 'android' | null;

/**
 * In-app browsers: the webview an app opens a link in, instead of handing it to
 * the phone's real browser. No banner at all in these - Apple's can't appear
 * there, and ours would be an app promotion inside somebody else's app.
 */
const IN_APP_BROWSER = new RegExp([
  // Facebook
  'FBAN',
  'FBAV',
  'FB_IAB',
  'Instagram',
  // Left out to keep this to the two that matter. Without them, these apps fall
  // back to the ordinary rules for whatever browser their webview reports.
  // 'LinkedInApp',
  // 'Twitter',
  // 'Line/',
].join('|'));

/**
 * Other browsers on iOS. Every one of them is WebKit underneath, so the engine
 * tells us nothing and the vendor token is all there is. None of them can show
 * Apple's Smart App Banner, so they get ours.
 */
const OTHER_IOS_BROWSER = /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/;

const getUserAgent = (): string => (canUseDOM ? window.navigator?.userAgent ?? '' : '');

/**
 * Which store to send this visitor to, or `null` when they aren't on a phone.
 *
 * Phones only, deliberately: iPads are left out, and so are Android tablets,
 * which by Google's own convention omit the `Mobile` token that phones carry.
 *
 * Sniffing the user agent is the only option - there is no feature to detect.
 * Never call this during server rendering, or the answer would be baked into
 * markup that a CDN may then serve to every other visitor.
 */
export const getMobilePlatform = (): MobilePlatform => {
  const ua = getUserAgent();

  if (/iPhone|iPod/.test(ua)) {
    return 'ios';
  }

  if (/Android/.test(ua) && /Mobile/.test(ua)) {
    return 'android';
  }

  return null;
};

export const isInAppBrowser = (): boolean => IN_APP_BROWSER.test(getUserAgent());

/**
 * True only where Apple's Smart App Banner can appear on a phone: Safari
 * itself, on an iPhone.
 *
 * Deliberately conservative, because being wrong costs more one way than the
 * other. A false negative shows our banner where Apple's would have done; a
 * false positive leaves the visitor with no banner at all.
 */
export const isIosSafari = (): boolean => {
  if (getMobilePlatform() !== 'ios' || isInAppBrowser()) {
    return false;
  }

  const ua = getUserAgent();

  if (OTHER_IOS_BROWSER.test(ua)) {
    return false;
  }

  // Installed to the home screen - Safari's chrome, and its banner, are gone.
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return false;
  }

  // A bare WKWebView - the in-app browser of any app that never customised its
  // user agent - carries no `Safari/` token, so this turns those away without
  // having to name them.
  return /Safari\//.test(ua);
};
